import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import {
  agent as agentApi,
  kg,
  messaging,
  primaryMedia,
  type Conversation,
  type Dossier,
  type Member,
  type Message,
} from "../api";
import { Avatar, NetIcon, netInfo, senderColor, isEmailNetwork } from "../cockpit-ui";
import { BellOff, IdCard, Mic, Paperclip, Pin, Play, UserPlus, X } from "lucide-react";
import { List, ListRow } from "./ui";
import { SenderAvatar } from "./SenderAvatar";
import { MergeContact } from "./MergeContact";
import { CompanyLink } from "./CompanyLink";
import { clockTime } from "../format";
// Visionneuse plein écran : chargée au premier clic sur un média (comme le fil).
const Lightbox = lazy(() => import("./Lightbox").then((m) => ({ default: m.Lightbox })));

// Combien de visuels avant le bouton « Tout voir » (3 colonnes → 2 rangées).
const MEDIA_PREVIEW = 6;

// Panneau infos (colonne droite) : recherche, identité du contact, sections
// encartées (médias, vocaux, fichiers, entreprise, mémoire, fusion, notes).
export function InfoPanel({
  conversation,
  pinned,
  onPin,
  onClose,
  onOpenContact,
}: {
  conversation: Conversation;
  pinned: boolean;
  onPin: () => void;
  onClose: () => void;
  /** Ouvre la fiche CRM d'un contact (#1 — accéder au profil depuis la conversation). */
  onOpenContact?: (personId: string) => void;
}) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [dossier, setDossier] = useState<Dossier | null>(null);
  const [muted, setMuted] = useState(false);
  const [notes, setNotes] = useState("");
  const [members, setMembers] = useState<Member[]>([]);
  const [showAllMedia, setShowAllMedia] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  // Fusion par membre (groupes) : clic sur un membre → résout SA personne → fiche fusion.
  const [memberMerge, setMemberMerge] = useState<{ name: string; personId: string } | null>(null);
  const [memberMergeErr, setMemberMergeErr] = useState<string | null>(null);
  // Modèle A (#1) : le correspondant d'un DM est-il déjà un CONTACT confirmé ? null
  // tant qu'on ne sait pas. false → on propose « Ajouter le contact » (créer/fusionner).
  const [personConfirmed, setPersonConfirmed] = useState<boolean | null>(null);
  const [adding, setAdding] = useState(false);
  const isGroup = conversation.kind !== "dm";
  // Les e-mails sont des « dm » mais ont plusieurs participants (From/To/Cc) — on
  // les liste comme un roster de groupe pour pouvoir les fusionner aux contacts KG.
  const isEmail = isEmailNetwork(conversation.network);
  const withRoster = isGroup || isEmail;

  async function openMemberMerge(mb: Member) {
    setMemberMergeErr(null);
    try {
      const d = await kg.personByIdentity(conversation.network, mb.remoteId);
      setMemberMerge({ name: mb.name || mb.remoteId, personId: d.person.id });
    } catch {
      setMemberMergeErr(`« ${mb.name || mb.remoteId} » n'est pas encore un contact résolu.`);
    }
  }

  // #1 (groupes) : ouvrir la fiche CRM d'un membre — résout sa personne via son
  // identité réseau, puis délègue la navigation au Shell.
  async function openMemberProfile(mb: Member) {
    if (!onOpenContact) return;
    setMemberMergeErr(null);
    try {
      const d = await kg.personByIdentity(conversation.network, mb.remoteId);
      onOpenContact(d.person.id);
    } catch {
      setMemberMergeErr(`« ${mb.name || mb.remoteId} » n'est pas encore un contact résolu.`);
    }
  }

  const loadDossier = useCallback(() => {
    agentApi
      .dossier(conversation.id)
      .then((r) => setDossier(r.dossier))
      .catch(() => setDossier(null));
  }, [conversation.id]);

  // Détache l'entreprise du contact (supprime le lien) puis rafraîchit le dossier
  // → l'entreprise disparaît et le contrôle « associer » réapparaît.
  async function detachCompany(companyId: string) {
    if (!dossier?.personId) return;
    try {
      await kg.unlinkCompany(dossier.personId, companyId);
      loadDossier();
    } catch {
      /* best-effort : un échec laisse l'état tel quel (le prochain rendu resync) */
    }
  }

  useEffect(() => {
    messaging
      .messages(conversation.id)
      .then((r) => setMessages(r.messages))
      .catch(() => setMessages([]));
    loadDossier();
    // DM : pas de roster — `members` garde sa valeur initiale [] (le panneau
    // est re-monté par conversation via key={id}, pas d'état résiduel).
    if (withRoster) {
      messaging
        .members(conversation.id)
        .then((r) => setMembers(r.members))
        .catch(() => setMembers([]));
    }
  }, [conversation.id, withRoster, loadDossier]);

  // Statut « contact confirmé » du correspondant (DM) — pour proposer l'ajout (#1).
  const personId = dossier?.personId ?? null;
  useEffect(() => {
    // Le panneau est re-monté par conversation (key={id}) → personConfirmed repart à
    // null tout seul ; inutile (et interdit par react-hooks) de le reset ici.
    if (isGroup || !personId) return;
    let live = true;
    kg.person(personId)
      .then((d) => live && setPersonConfirmed(d.person.confirmed ?? true))
      .catch(() => {
        /* identité non encore résolue : on laisse null (pas de bouton) */
      });
    return () => {
      live = false;
    };
  }, [personId, isGroup]);

  // Geste « Ajouter le contact » : promeut l'identité observée en contact du CRM.
  async function addContact() {
    if (!personId || adding) return;
    setAdding(true);
    try {
      await kg.confirmPerson(personId);
      setPersonConfirmed(true);
      loadDossier();
    } catch {
      /* best-effort : l'état resync au prochain rendu */
    } finally {
      setAdding(false);
    }
  }

  const n = netInfo(conversation.network);

  // Médias séparés par nature : visuels en grille cliquable (→ Lightbox), vocaux
  // dans leur section avec lecteur, fichiers en liste.
  const visual = useMemo(
    () =>
      messages.filter((m) => {
        const md = primaryMedia(m);
        return md?.kind === "image" || md?.kind === "video";
      }),
    [messages],
  );
  const voices = useMemo(
    () =>
      messages.filter((m) => {
        const md = primaryMedia(m);
        return md?.kind === "voice" || md?.kind === "audio";
      }),
    [messages],
  );
  const files = useMemo(() => messages.filter((m) => primaryMedia(m)?.kind === "file"), [messages]);
  // Items de la Lightbox : tous les visuels, navigables aux flèches.
  const mediaItems = useMemo(
    () =>
      visual.map((m) => {
        const md = primaryMedia(m)!;
        return {
          src: messaging.mediaUrl(m.id),
          kind: md.kind as "image" | "video",
          fileName: md.fileName,
        };
      }),
    [visual],
  );
  const shownMedia = showAllMedia ? visual : visual.slice(0, MEDIA_PREVIEW);

  return (
    <aside className="info-panel">
      <div className="info-top">
        {/* Fermeture par une croix en haut. */}
        <button
          className="info-round"
          title="Fermer"
          aria-label="Fermer le panneau"
          onClick={onClose}
        >
          <X size={18} />
        </button>
        <div className="info-top-actions">
          <button
            className={`info-round ${muted ? "active" : ""}`}
            title={muted ? "Réactiver" : "Couper les notifs"}
            aria-label={muted ? "Réactiver les notifications" : "Couper les notifications"}
            onClick={() => setMuted((v) => !v)}
          >
            <BellOff size={18} />
          </button>
          <button
            className={`info-round ${pinned ? "active" : ""}`}
            title={pinned ? "Désépingler" : "Épingler"}
            aria-label={pinned ? "Désépingler" : "Épingler"}
            onClick={onPin}
          >
            <Pin size={18} />
          </button>
        </div>
      </div>

      <div className="info-id">
        <Avatar chat={conversation} size={92} />
        <h3 className="info-name">{conversation.title || n.label}</h3>
        <span className="info-net">
          <NetIcon network={conversation.network} size={12} /> {n.label}
        </span>
        {/* Modèle A (#1) : un correspondant non confirmé n'est pas un contact du CRM.
            On propose de l'AJOUTER (créer) — la fusion avec un contact existant reste
            possible plus bas (section « Fusionner »). Une fois confirmé (ou déjà
            contact), on ouvre sa fiche. */}
        {!isGroup && personId && personConfirmed === false && (
          <button
            type="button"
            className="info-link info-add-contact"
            onClick={() => void addContact()}
            disabled={adding}
          >
            <UserPlus size={14} /> {adding ? "Ajout…" : "Ajouter le contact"}
          </button>
        )}
        {!isGroup && onOpenContact && personId && personConfirmed !== false && (
          <button
            type="button"
            className="info-link info-open-fiche"
            onClick={() => onOpenContact(personId)}
          >
            <IdCard size={14} /> Ouvrir la fiche
          </button>
        )}
      </div>

      <div className="info-sections">
        {withRoster && memberMerge && (
          <List
            header={
              <>
                Fusionner « {memberMerge.name} »
                <button className="info-link" type="button" onClick={() => setMemberMerge(null)}>
                  Fermer
                </button>
              </>
            }
          >
            <MergeContact
              key={memberMerge.personId}
              personId={memberMerge.personId}
              conversationId={conversation.id}
              onChanged={loadDossier}
            />
          </List>
        )}

        {withRoster && members.length > 0 && (
          <List
            header={
              <>
                {isEmail ? "Participants" : "Membres"}{" "}
                <span className="info-count">{members.length}</span>
              </>
            }
          >
            {memberMergeErr && <p className="info-empty">{memberMergeErr}</p>}
            {members.map((mb) => (
              <ListRow
                key={mb.remoteId}
                leading={
                  <SenderAvatar
                    src={messaging.participantAvatarUrl(conversation.id, mb.remoteId)}
                    label={mb.name || mb.remoteId}
                  />
                }
                title={
                  <span style={{ color: senderColor(mb.name || mb.remoteId) }}>
                    {mb.name || mb.remoteId}
                  </span>
                }
                value={mb.isSelf ? "Vous" : "Fusionner"}
                trailing={
                  !mb.isSelf && onOpenContact ? (
                    <button
                      type="button"
                      className="link"
                      title="Ouvrir la fiche"
                      aria-label={`Ouvrir la fiche de ${mb.name || mb.remoteId}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        void openMemberProfile(mb);
                      }}
                    >
                      <IdCard size={16} />
                    </button>
                  ) : undefined
                }
                onClick={mb.isSelf ? undefined : () => openMemberMerge(mb)}
              />
            ))}
          </List>
        )}

        <List
          header={
            <>
              Médias
              {visual.length > 0 && <span className="info-count">{visual.length}</span>}
              {visual.length > MEDIA_PREVIEW && (
                <button
                  className="info-link"
                  type="button"
                  onClick={() => setShowAllMedia((v) => !v)}
                >
                  {showAllMedia ? "Réduire" : "Tout voir"}
                </button>
              )}
            </>
          }
        >
          {visual.length > 0 ? (
            <div className="media-grid">
              {shownMedia.map((m) => {
                const md = primaryMedia(m)!;
                const idx = mediaItems.findIndex((it) => it.src === messaging.mediaUrl(m.id));
                return (
                  <button
                    key={m.id}
                    type="button"
                    className="media-cell media-cell-btn"
                    aria-label={`Ouvrir ${md.kind === "video" ? "la vidéo" : "l'image"}`}
                    onClick={() => setLightboxIdx(Math.max(idx, 0))}
                  >
                    {md.kind === "video" ? (
                      <>
                        <video src={messaging.mediaUrl(m.id)} preload="metadata" muted />
                        <span className="media-cell-play">
                          <Play size={18} />
                        </span>
                      </>
                    ) : (
                      <img
                        src={messaging.mediaUrl(m.id)}
                        alt={md.fileName ?? "image"}
                        loading="lazy"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="info-empty">Aucun média partagé.</p>
          )}
        </List>

        {voices.length > 0 && (
          <List
            header={
              <>
                Messages vocaux <span className="info-count">{voices.length}</span>
              </>
            }
          >
            {voices.map((m) => (
              <div key={m.id} className="voice-row">
                <span className="voice-icon">
                  <Mic size={15} />
                </span>
                <audio
                  className="voice-audio"
                  src={messaging.mediaUrl(m.id)}
                  controls
                  preload="none"
                />
                <span className="voice-time">{clockTime(m.sentAt)}</span>
              </div>
            ))}
          </List>
        )}

        {files.length > 0 && (
          <List
            header={
              <>
                Fichiers <span className="info-count">{files.length}</span>
              </>
            }
          >
            {files.map((m) => {
              const md = primaryMedia(m)!;
              return (
                <a
                  key={m.id}
                  className="file-row"
                  href={messaging.mediaUrl(m.id)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Paperclip size={15} />
                  <span className="file-name">{md.fileName ?? "pièce jointe"}</span>
                  <span className="voice-time">{clockTime(m.sentAt)}</span>
                </a>
              );
            })}
          </List>
        )}

        {dossier && dossier.companies.length > 0 && (
          <List header="Entreprise">
            {dossier.companies.map((c, i) => (
              <ListRow
                key={c.companyId ?? i}
                leading={
                  c.companyId && c.hasLogo ? (
                    <img
                      className="company-logo"
                      src={kg.companyLogoUrl(c.companyId)}
                      alt=""
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : undefined
                }
                title={
                  <span>
                    {c.name}
                    {c.role ? ` — ${c.role}` : ""}
                    {c.domain && <span className="company-domain"> {c.domain}</span>}
                  </span>
                }
                value={
                  c.optedIn ? (
                    <span className="optin on">IA ✓</span>
                  ) : (
                    <span className="optin off">IA ✗</span>
                  )
                }
                trailing={
                  c.companyId && dossier.personId ? (
                    <button
                      type="button"
                      className="company-detach"
                      title="Détacher l'entreprise"
                      aria-label="Détacher l'entreprise"
                      onClick={() => void detachCompany(c.companyId!)}
                    >
                      <X size={14} />
                    </button>
                  ) : undefined
                }
              />
            ))}
          </List>
        )}

        {/* Associer la conversation à une entreprise (DM uniquement, et seulement
            tant qu'AUCUNE entreprise n'est liée — une fois liée, on n'affiche plus
            le contrôle d'association). */}
        {!isGroup && dossier?.personId && dossier.companies.length === 0 && (
          <CompanyLink personId={dossier.personId} dossier={dossier} onChanged={loadDossier} />
        )}

        {dossier && dossier.memory.length > 0 && (
          <List header="Mémoire">
            {dossier.memory.slice(0, 4).map((m, i) => (
              <p key={i} className="info-line">
                <span className="muted">{m.kind} · </span>
                {m.content}
              </p>
            ))}
          </List>
        )}

        {/* DM : fusion du contact de la conversation. Groupe : on passe par le clic
            sur un membre (la personne « de la conversation » y est ambiguë). */}
        {!isGroup && dossier?.personId && (
          <MergeContact
            key={dossier.personId}
            personId={dossier.personId}
            conversationId={conversation.id}
            onChanged={loadDossier}
          />
        )}

        <List header="Notes">
          <textarea
            className="info-notes-area"
            placeholder="Ajouter une note…"
            aria-label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </List>
      </div>

      {lightboxIdx !== null && mediaItems[lightboxIdx] && (
        <Suspense fallback={null}>
          <Lightbox items={mediaItems} index={lightboxIdx} onClose={() => setLightboxIdx(null)} />
        </Suspense>
      )}
    </aside>
  );
}
